import { useState, useEffect, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Form, message, Modal } from 'antd';
import {
  StyledCreateUpdateListingApplicationDrawer,
  StyledFooter,
  StyledDrawerTitleWrapper,
} from './style';
import { Tabs } from '../../Tabs';
import { NewApplicationForm } from '../../Form/Listings/ListingApplication/NewApplicationForm';
import { NewApplicationProductsForm } from '../../Form/Listings/ListingApplication/NewApplicationProductsForm';
import { NewApplicationDeliveryForm } from '../../Form/Listings/ListingApplication/NewApplicationDeliveryForm';
import { NewApplicationContractForm } from '../../Form/Listings/ListingApplication/NewApplicationContractForm';
import { ApplicationSummary } from '../../Form/Listings/ListingApplication/ApplicationSummary/index';
import { Button } from '../../Button';
import { Spinner } from '../../Spinner';
import { Icon } from 'ui/Icon';
import { Tag } from '../../Tag';
import { Label } from '../../Label';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { useAuth } from 'hooks/useAuth';
import { useAutoSave } from '../../../hooks/useAutoSave';
import dayjs from 'dayjs';

import {
  Listing,
  ListingApplication,
  ListingApplicationFormData,
  ApplicationProductData,
  ApplicationDeliveryCondition,
  ApplicationCertificate,
  FarmQualityMark,
  ExtendedApplicationProductData,
  ProcessedProduct,
  ProcessedDelivery,
  ListingProduct,
  DeliveryConditionsData,
  DeliveryProductReference,
  FarmProduct,
} from '../../../types/ListingData';
import {
  GROUPED_QUALITY_MARKS_URL,
  QualityMarkOption,
  findQualityMark,
  hasAllRequiredQualityMarks,
} from 'utils/qualityMarkHelpers';

declare global {
  interface Window {
    saveDraftResolve?:
      | ((appId?: string | number | null) => void)
      | (() => void);
    saveDraftReject?: () => void;
  }
}

interface DrawerCreateUpdateListingApplicationProps {
  open: boolean;
  onClose: () => void;
  listingId: string | number | null;
  applicationId?: number | null;
  setReload: React.Dispatch<React.SetStateAction<number>>;
}

const EDITABLE_STATUSES = ['new', 'rejected'];

type FieldError = {
  name: (string | number)[];
  errors: string[];
};

type SubmitApplicationWarning = {
  key: string;
  deliveryRadius?: number;
  calculatedDistance?: number;
};

type SubmitApplicationResponse =
  | ListingApplication
  | { application: ListingApplication; warnings: SubmitApplicationWarning[] };

const parseCoordinates = (
  coordinates: string
): { lat: number; long: number } | null => {
  const parts = coordinates.split(',').map((p) => p.trim());
  if (parts.length >= 2) {
    const lat = parseFloat(parts[0]);
    const long = parseFloat(parts[1]);
    if (!Number.isNaN(lat) && !Number.isNaN(long)) {
      return { lat, long };
    }
  }
  return null;
};

const normalizeQuantity = (
  quantity: number | string | undefined | null
): number | undefined => {
  if (quantity === null) return 0;
  if (quantity === undefined) return undefined;
  return typeof quantity === 'string' ? parseFloat(quantity) : quantity;
};

const formatDeliveryDate = (
  formDate: dayjs.Dayjs | string | null | undefined,
  listingConditionDate: string | null | undefined,
  isAcceptingAll: boolean
): string | null => {
  if (isAcceptingAll && listingConditionDate) {
    return listingConditionDate;
  }

  if (!formDate) {
    return null;
  }

  if (dayjs.isDayjs(formDate)) {
    return formDate.toISOString();
  }

  return dayjs(formDate).toISOString();
};

export const DrawerCreateUpdateListingApplication = ({
  open,
  onClose,
  listingId,
  applicationId,
  setReload,
}: DrawerCreateUpdateListingApplicationProps) => {
  const { data: ignoreDeadlineSetting } = useQueryApiClient({
    request: { url: '/api/v2/settings/value/ignore_offer_deadline_validation' },
  });
  const ignoreDeadlineValidation = (() => {
    if (ignoreDeadlineSetting == null) return false;
    const stripped = String(ignoreDeadlineSetting)
      .replace(/<[^>]*>/g, '')
      .trim();
    return stripped === '1' || stripped === 'true';
  })();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [draftId, setDraftId] = useState<number | null>(null);
  const [lastSavedData, setLastSavedData] =
    useState<Partial<ListingApplicationFormData> | null>(null);
  const [isFirstSave, setIsFirstSave] = useState<boolean>(true);
  const [cachedApplication, setCachedApplication] =
    useState<ListingApplication | null>(null);
  const [cachedListing, setCachedListing] = useState<Listing | null>(null);

  const [currentCertificates, setCurrentCertificates] = useState<
    ApplicationCertificate[]
  >([]);
  const [, setDrawerKey] = useState<number>(0);
  const [tabValidationErrors, setTabValidationErrors] = useState<{
    [tabKey: string]: number;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isValidatingAllSteps, setIsValidatingAllSteps] =
    useState<boolean>(false);

  const [form] = Form.useForm();

  const intl = useIntl();
  const { user } = useAuth();

  const products =
    (Form.useWatch('application_products', form) as
      | ApplicationProductData[]
      | undefined) || [];

  const formProductsForSummary = useMemo(() => {
    const sourceProducts = products;

    return sourceProducts.map((product: ApplicationProductData) => {
      if (product.offer_type) {
        return product;
      }

      const offerType: 'full_amount' | 'other_amount' =
        product.offered_quantity !== null &&
        product.offered_quantity !== undefined &&
        Number(product.offered_quantity) > 0
          ? 'other_amount'
          : 'full_amount';

      return {
        ...product,
        offer_type: offerType,
      };
    });
  }, [products]);

  const isEditableStatus = (status: string | undefined): boolean => {
    return !!status && EDITABLE_STATUSES.includes(status);
  };

  useEffect(() => {
    if (open && !applicationId) {
      setDrawerKey((prev) => prev + 1);
    }
  }, [open, applicationId]);

  const stepFieldMap: Record<string, string[]> = {
    basic_info: [
      'producer_comments',
      'offer_valid_from',
      'offer_valid_until',
      'warehouse_address',
      'lks_lat',
      'lks_long',
      'certificates',
    ],
    products: ['application_products'],
    delivery: ['delivery_type', 'delivery_conditions'],
  };

  const { appendData: saveDraftData, isLoading: isDraftSaving } =
    useQueryApiClient({
      request: {
        url:
          isFirstSave || !draftId
            ? `api/v2/listings/${listingId}/applications`
            : `api/v2/listing-applications/${draftId}?_method=PATCH`,
        method: 'POST',
      },
      onSuccess: (response, passOnSuccess) => {
        const isAutoSave = passOnSuccess?.isAutoSave === true;

        if (isFirstSave && response?.id) {
          setDraftId(response.id);
          setIsFirstSave(false);
        }

        if (
          response?.application_products &&
          response.application_products.length > 0
        ) {
          const productsWithOfferType = response.application_products.map(
            (ap: ApplicationProductData) => ({
              ...ap,
              offer_type:
                ap.offered_quantity != null && ap.offered_quantity !== undefined
                  ? 'other_amount'
                  : ap.offer_type || 'full_amount',
            })
          );
          form.setFieldValue('application_products', productsWithOfferType);
        }

        if (response?.delivery_conditions) {
          const hasFreeForm = cachedListing?.delivery_conditions?.some(
            (dc: DeliveryConditionsData) => dc.delivery_type === 'free_form'
          );
          const deliveriesWithDayjs = response.delivery_conditions.map(
            (dc: ApplicationDeliveryCondition) => ({
              ...dc,
              delivery_start_date: dc.delivery_start_date
                ? dayjs(dc.delivery_start_date)
                : null,
              delivery_end_date: dc.delivery_end_date
                ? dayjs(dc.delivery_end_date)
                : null,
              is_accepting_all:
                dc.is_accepting_all !== undefined &&
                dc.is_accepting_all !== null
                  ? dc.is_accepting_all
                  : hasFreeForm
                    ? true
                    : false,
            })
          );
          form.setFieldValue('delivery_conditions', deliveriesWithDayjs);
        }

        const previousCertificates = lastSavedData?.certificates || [];

        const currentFormValues = form.getFieldsValue(true);
        const lastSaved = {
          ...currentFormValues,
          certificates: currentCertificates,
        };

        const compareFieldsArray = [
          'producer_comments',
          'offer_valid_from',
          'offer_valid_until',
          'warehouse_address',
          'lks_lat',
          'lks_long',
          'delivery_cost',
          'application_products',
          'delivery_conditions',
          'delivery_type',
          'certificates',
        ];

        compareFieldsArray.forEach((field) => {
          if (!(field in lastSaved)) {
            lastSaved[field] = undefined;
          }
        });

        setLastSavedData(lastSaved);
        setDraftSaveStatus({
          status: 'saved',
          lastSaved: new Date(),
        });
        message.destroy();
        message.success(
          isAutoSave
            ? intl.formatMessage({ id: 'general.draft_autosaved' })
            : intl.formatMessage({ id: 'general.draft_saved' })
        );

        const appId = response?.id || draftId;

        if (appId && currentCertificates && currentCertificates.length > 0) {
          const pending = currentCertificates.filter(
            (c) => c.isPending && c.originFile
          );

          if (pending.length > 0) {
            const fd = new FormData();
            pending.forEach((c) => {
              if (c.originFile) {
                fd.append('files[]', c.originFile);
              }
            });

            uploadCertificates(fd, { appId }, { appId });
          } else {
            runDeferredDeletes(appId, previousCertificates);
          }
        } else {
          if (appId) {
            runDeferredDeletes(appId, previousCertificates);
          } else {
            setReload((old) => old + 1);
          }
        }

        if (window.saveDraftResolve) {
          const appId = response?.id || draftId;
          window.saveDraftResolve(appId);
          window.saveDraftResolve = undefined;
          window.saveDraftReject = undefined;
        }
      },
      onError: (error) => {
        setDraftSaveStatus({
          status: 'error',
          error: error.message,
        });
        message.error(intl.formatMessage({ id: 'general.draft_save_failed' }));
        message.destroy();

        if (window.saveDraftReject) {
          window.saveDraftReject();
          window.saveDraftResolve = undefined;
          window.saveDraftReject = undefined;
        }
      },
    });

  const { appendData: uploadCertificates } = useQueryApiClient({
    request: {
      url: 'api/v2/listing-applications/:appId/certificates/upload',
      method: 'POST',
      multipart: true,
    },
    onSuccess: async (resp, ctx) => {
      const appId = ctx?.appId;
      const previousCertificates = lastSavedData?.certificates || [];

      let responseData = resp;
      if (resp instanceof Blob) {
        try {
          const text = await resp.text();
          responseData = JSON.parse(text);
        } catch (e) {
          return;
        }
      }

      const uploadedCerts = Array.isArray(responseData?.certificates)
        ? responseData.certificates
        : [];

      setCurrentCertificates((prev) => {
        let uploadedIndex = 0;

        const updated = prev.map((cert) => {
          if (cert.isPending) {
            const uploadedCert = uploadedCerts[uploadedIndex];
            uploadedIndex++;

            return {
              ...cert,
              isPending: false,
              ...(uploadedCert && {
                file_id: uploadedCert.file_id || cert.file_id,
                id: uploadedCert.id || cert.id,
                listing_application_id:
                  uploadedCert.listing_application_id ||
                  cert.listing_application_id,
                created_at: uploadedCert.created_at || cert.created_at,
              }),
            };
          }
          return cert;
        });

        setLastSavedData((lastSaved) => {
          if (lastSaved) {
            return {
              ...lastSaved,
              certificates: updated,
            };
          }
          return lastSaved;
        });

        return updated;
      });

      if (appId) {
        runDeferredDeletes(appId, previousCertificates);
      } else {
        setReload((old) => old + 1);
      }
    },
  });

  const { appendData: deleteCertificatesBulk } = useQueryApiClient({
    request: {
      url: 'api/v2/listing-applications/:appId/certificates/delete',
      method: 'POST',
    },
  });

  const { appendData: submitApplication, isLoading: isSubmittingApplication } =
    useQueryApiClient({
      request: {
        url: 'api/v2/listing-applications/:appId/submit',
        method: 'POST',
      },
      onSuccess: (response: SubmitApplicationResponse) => {
        message.success(intl.formatMessage({ id: 'general.success' }));
        const warnings = 'warnings' in response ? response.warnings : undefined;
        if (Array.isArray(warnings) && warnings.length > 0) {
          const warehouseWarning = warnings.find(
            (w: SubmitApplicationWarning) =>
              w.key === 'WAREHOUSE_ADDRESS_OUTSIDE_DELIVERY_RADIUS'
          );
          if (warehouseWarning) {
            const num = Number(warehouseWarning.calculatedDistance);
            const formattedDistance = !Number.isNaN(num)
              ? num.toFixed(2)
              : warehouseWarning.calculatedDistance;
            message.warning({
              content: intl.formatMessage(
                { id: 'validation.warehouse_address_outside_delivery_radius' },
                {
                  deliveryRadius: warehouseWarning.deliveryRadius,
                  calculatedDistance: formattedDistance,
                }
              ),
              duration: 8,
            });
          }
        }
        setIsSubmitting(false);
        handleSuccess();
      },
      onError: (error: { message?: string }) => {
        message.error(
          error?.message || intl.formatMessage({ id: 'validation.error' })
        );
        setIsSubmitting(false);
      },
    });

  const runDeferredDeletes = (
    appId: number,
    previousCertificates?: ApplicationCertificate[]
  ) => {
    const before = previousCertificates || lastSavedData?.certificates || [];
    const after = currentCertificates || [];

    const beforeWithFileId = before.filter((c) => c && c.file_id);
    const afterWithFileId = after.filter((c) => c && c.file_id);
    const afterFileIdSet = new Set(afterWithFileId.map((c) => c.file_id));
    const removedFileIds = beforeWithFileId
      .map((c) => c.file_id as number)
      .filter((id: number) => !afterFileIdSet.has(id));

    if (removedFileIds.length === 0) {
      setReload((old) => old + 1);
      return;
    }
    deleteCertificatesBulk({ file_ids: removedFileIds }, { appId });
    setReload((old) => old + 1);
  };

  const handleCertificateDelete = (cert: ApplicationCertificate) => {
    setCurrentCertificates((prev) =>
      prev.filter((c) => {
        if (c.id && cert.id && c.id === cert.id) return false;
        if (c.file_id && cert.file_id && c.file_id === cert.file_id)
          return false;
        if (
          c.file_name &&
          cert.file_name &&
          c.file_name === cert.file_name &&
          !c.id &&
          !cert.id &&
          !c.file_id &&
          !cert.file_id
        ) {
          return false;
        }
        return true;
      })
    );
  };

  const { data: applicationData, isLoading: isLoadingApplication } =
    useQueryApiClient({
      request: {
        url: `/api/v2/listing-applications/${applicationId}`,
        method: 'GET',
      },
      enabled: !!applicationId && open,
      onSuccess: (data) => {
        if (data) {
          setCachedApplication(data);
          setDraftId(data.id);
          setIsFirstSave(false);
          setDraftSaveStatus({ status: 'saved' });
        }
      },
    });

  const actualListingId: number | undefined =
    listingId || applicationData?.listing_id;

  const { data: listingData, isLoading: isLoadingListing } = useQueryApiClient({
    request: {
      url: `/api/v2/listings/${actualListingId!}`,
      method: 'GET',
    },
    enabled: !!actualListingId && open,
    onSuccess: (data) => {
      setCachedListing(data);
    },
  });

  const { data: allQualityMarks } = useQueryApiClient({
    request: {
      url: GROUPED_QUALITY_MARKS_URL,
    },
    enabled: open,
  });

  const { data: farmQualityMarks, isLoading: isLoadingFarmQualityMarks } =
    useQueryApiClient({
      request: {
        url: `api/v2/farms/qmarks/${user?.farmId}`,
      },
      enabled: !!user?.farmId && open,
    });

  const {
    data: productFormAttributes,
    isLoading: isLoadingProductFormAttributes,
  } = useQueryApiClient({
    request: {
      url: 'api/v2/products/form/attributes',
    },
    enabled: open,
  });

  const storageUnits = productFormAttributes?.storageUnits || [];

  const categoryIds = useMemo(() => {
    if (!listingData?.products) return [];
    const ids = new Set<number>();
    listingData.products.forEach((product: ListingProduct) => {
      if (product.category_id) {
        ids.add(product.category_id);
      }
    });
    return Array.from(ids);
  }, [listingData?.products]);

  const [categoryProductsLoading, setCategoryProductsLoading] = useState(false);
  const [categoryProductsData, setCategoryProductsData] = useState<{
    [categoryId: number]: FarmProduct[];
  }>({});

  const { appendData: fetchCategoryProductsBatch } = useQueryApiClient({
    request: {
      url: 'api/v2/categories/products/batch',
      disableOnMount: true,
    },
    onSuccess: (response: { [categoryId: number]: FarmProduct[] }) => {
      setCategoryProductsData(response || {});
      setCategoryProductsLoading(false);
    },
    onError: (error) => {
      setCategoryProductsLoading(false);
    },
  });

  useEffect(() => {
    if (!open || !listingData || categoryIds.length === 0) {
      setCategoryProductsData({});
      return;
    }

    setCategoryProductsLoading(true);
    setCategoryProductsData({});

    const data: { category_ids: number[]; farm_id?: number } = {
      category_ids: categoryIds,
    };

    if (user?.farmId) {
      data.farm_id = user.farmId;
    }

    fetchCategoryProductsBatch(data);
  }, [open, listingData, categoryIds, user?.farmId]);

  const availableProductsByCategory = categoryProductsData;

  const farmProductNames = useMemo(() => {
    const namesMap: { [key: number]: string } = {};
    Object.values(availableProductsByCategory).forEach((products) => {
      products.forEach((product) => {
        if (product.id && product.name) {
          namesMap[product.id] = product.name;
        }
      });
    });
    return namesMap;
  }, [availableProductsByCategory]);

  const displayProducts = useMemo(() => {
    if (!listingData?.products) {
      return [];
    }

    const applicationProducts =
      products.length > 0 ? products : applicationData?.application_products;

    const mapProducts = (
      listingProducts: ListingProduct[],
      applicationProducts?: ApplicationProductData[]
    ): ExtendedApplicationProductData[] => {
      return listingProducts
        .filter(
          (listingProduct): listingProduct is ListingProduct & { id: number } =>
            !!listingProduct.id
        )
        .map((listingProduct) => {
          const applicationProduct = applicationProducts?.find(
            (ap) => ap.listing_product_id === listingProduct.id
          );

          const offerType = applicationProduct
            ? applicationProduct.offer_type ||
              (applicationProduct.offered_quantity !== null &&
              applicationProduct.offered_quantity !== undefined &&
              applicationProduct.offered_quantity > 0
                ? 'other_amount'
                : 'full_amount')
            : 'full_amount';

          const appProduct = applicationProduct as
            | ExtendedApplicationProductData
            | undefined;

          return {
            listing_product_id: listingProduct.id,
            offer_type: offerType as 'full_amount' | 'other_amount',
            farm_product_id: applicationProduct?.farm_product_id || undefined,
            offered_quantity: applicationProduct
              ? normalizeQuantity(applicationProduct.offered_quantity)
              : undefined,
            offered_unit_id:
              applicationProduct?.offered_unit_id ||
              listingProduct.storage_unit?.id ||
              undefined,
            offered_price:
              typeof applicationProduct?.offered_price === 'number'
                ? applicationProduct.offered_price
                : applicationProduct?.offered_price !== null &&
                    applicationProduct?.offered_price !== undefined
                  ? parseFloat(String(applicationProduct.offered_price))
                  : undefined,
            offered_price_unit_id:
              applicationProduct?.offered_price_unit_id ||
              listingProduct.storage_unit?.id ||
              undefined,
            id: appProduct?.id || null,
            is_full_quantity:
              appProduct?.is_full_quantity ?? offerType === 'full_amount',
            product_name:
              (applicationProduct?.farm_product_id
                ? farmProductNames[applicationProduct.farm_product_id]
                : null) ||
              listingProduct.name ||
              listingProduct.category?.name ||
              '',
            product_category: listingProduct.category,
            product_storage_unit: listingProduct.storage_unit,
            product_quality_marks: (listingProduct.quality_mark_ids || []).map(
              (id) => {
                const qm = findQualityMark<QualityMarkOption>(
                  allQualityMarks,
                  id
                );
                return {
                  id,
                  name: qm?.name || '',
                };
              }
            ),
            product_required_quantity: listingProduct.required_quantity,
            product_min_acceptable_offer_quantity:
              listingProduct.min_acceptable_offer_quantity || 0,
            product_allow_smaller_quantity:
              listingProduct.allow_smaller_quantity || false,
            product_period_for_total_quantity:
              listingProduct.period_for_total_quantity || '',
            listing_product: listingProduct,
          };
        });
    };

    return mapProducts(listingData.products, applicationProducts);
  }, [
    listingData?.products,
    products,
    applicationData?.application_products,
    farmProductNames,
  ]);

  const hasProductsMissingQualityMarks = useMemo(() => {
    if (!listingData?.products || !products || products.length === 0) {
      return false;
    }

    return products.some((product: ApplicationProductData, index: number) => {
      const listingProduct = listingData.products?.[index];
      if (!listingProduct || !product.farm_product_id) {
        return false;
      }

      const requiredQualityMarkIds = listingProduct.quality_mark_ids || [];
      if (requiredQualityMarkIds.length === 0) {
        return false;
      }

      const selectedFarmProduct = listingProduct.category_id
        ? availableProductsByCategory[listingProduct.category_id]?.find(
            (p) => p.id === product.farm_product_id
          )
        : undefined;

      if (!selectedFarmProduct) {
        return false;
      }

      const selectedProductQualityMarkIds = selectedFarmProduct.qualitymarks
        ? selectedFarmProduct.qualitymarks.map((qm) => qm.quality_mark_id)
        : [];

      return !hasAllRequiredQualityMarks(
        requiredQualityMarkIds,
        selectedProductQualityMarkIds,
        allQualityMarks
      );
    });
  }, [
    products,
    listingData?.products,
    availableProductsByCategory,
    allQualityMarks,
  ]);

  const { data: farmData } = useQueryApiClient({
    request: {
      url: `api/v2/farms/${user?.farmId}`,
      method: 'GET',
    },
    enabled: !!user?.farmId && open,
  });

  const isDataLoading = useMemo(() => {
    if (!open) return false;
    if (isLoadingListing || isLoadingApplication) return true;
    if (isLoadingFarmQualityMarks || isLoadingProductFormAttributes)
      return true;
    if (!listingData) return true;
    if (applicationId && !applicationData) return true;
    if (!farmQualityMarks) return true;
    if (!productFormAttributes) return true;

    if (categoryIds.length > 0 && categoryProductsLoading) return true;
    if (
      categoryIds.length > 0 &&
      categoryIds.some((categoryId) => !availableProductsByCategory[categoryId])
    )
      return true;

    if (user?.farmId && !farmData) return true;

    return false;
  }, [
    open,
    isLoadingListing,
    isLoadingApplication,
    isLoadingFarmQualityMarks,
    isLoadingProductFormAttributes,
    listingData,
    applicationId,
    applicationData,
    farmQualityMarks,
    productFormAttributes,
    categoryIds,
    categoryProductsLoading,
    availableProductsByCategory,
    farmData,
    user?.farmId,
  ]);

  useEffect(() => {
    if (isDataLoading || !listingData || !form) {
      return;
    }

    const hasListingDataContent =
      listingData.id || listingData.products || listingData.title;
    if (!hasListingDataContent) {
      return;
    }

    if (
      !applicationData &&
      (!listingData.products || listingData.products.length === 0)
    ) {
      return;
    }

    if (
      !applicationData &&
      (!listingData.delivery_conditions ||
        listingData.delivery_conditions.length === 0)
    ) {
      return;
    }

    const baseData = {
      listing_title: listingData.title,
      listing_identifier: listingData.identifier,
      listing_transaction_type: listingData.transaction_type,
      listing_transaction_context: listingData.transaction_context,
      listing_publication_start_date: listingData.publication_start_date,
      listing_publication_end_date: listingData.publication_end_date,
      listing_delivery_radius_km: listingData.delivery_radius_km,
      listing_is_private: listingData.is_private,
      listing_single_producer_only: listingData.single_producer_only,
      listing_is_recycled: listingData.is_recycled,
      listing_auto_extend_publication: listingData.auto_extend_publication,
      listing_extend_days: listingData.extend_days,
      contract_start_date: listingData.contract_terms?.contract_start_date
        ? dayjs(listingData.contract_terms.contract_start_date)
        : null,
      contract_end_date: listingData.contract_terms?.contract_end_date
        ? dayjs(listingData.contract_terms.contract_end_date)
        : null,
      payment_terms: listingData.contract_terms?.payment_conditions,
      additional_terms: listingData.contract_terms?.additional_contract_terms,
    };

    const formData: Partial<ListingApplicationFormData> = {
      ...baseData,
      ...(applicationData && {
        producer_comments: applicationData.producer_comment || '',
        ...(applicationData.offer_valid_from && {
          offer_valid_from: dayjs(applicationData.offer_valid_from),
        }),
        ...(applicationData.offer_valid_until && {
          offer_valid_until: dayjs(applicationData.offer_valid_until),
        }),
        warehouse_address: applicationData.warehouse_address || '',
        lks_lat: applicationData.lks_lat || null,
        lks_long: applicationData.lks_long || null,
        delivery_cost: applicationData.delivery_cost || '',
      }),
      application_products: (() => {
        if (
          !listingData.products ||
          !Array.isArray(listingData.products) ||
          listingData.products.length === 0
        ) {
          return [];
        }

        const baseProducts = listingData.products
          .filter((lp: ListingProduct) => lp.id)
          .map((lp: ListingProduct) => ({
            listing_product_id: lp.id,
            offer_type: 'full_amount' as const,
          }));

        if (applicationData?.application_products) {
          const applicationProductsMap = new Map(
            applicationData.application_products.map(
              (ap: ApplicationProductData) => [ap.listing_product_id, ap]
            )
          );

          const mergedProducts = baseProducts.map(
            (baseProduct: {
              listing_product_id: number;
              offer_type: 'full_amount';
            }) => {
              const applicationProduct = applicationProductsMap.get(
                baseProduct.listing_product_id
              );
              if (applicationProduct) {
                const ap = applicationProduct as ApplicationProductData;
                const offerType =
                  ap.offered_quantity != null &&
                  ap.offered_quantity !== undefined
                    ? 'other_amount'
                    : ap.offer_type || 'full_amount';

                return {
                  ...baseProduct,
                  ...ap,
                  offer_type: offerType,
                };
              }

              return baseProduct;
            }
          );

          return mergedProducts;
        }

        return baseProducts;
      })(),
      delivery_conditions: (() => {
        const hasListingDeliveryConditions =
          listingData.delivery_conditions &&
          Array.isArray(listingData.delivery_conditions) &&
          listingData.delivery_conditions.length > 0;

        if (!hasListingDeliveryConditions) {
          if (applicationData?.delivery_conditions) {
            return applicationData.delivery_conditions.map(
              (adc: ApplicationDeliveryCondition) => {
                const deliveryStartDate = adc.delivery_start_date
                  ? dayjs.isDayjs(adc.delivery_start_date)
                    ? adc.delivery_start_date
                    : dayjs(adc.delivery_start_date)
                  : null;
                const deliveryEndDate = adc.delivery_end_date
                  ? dayjs.isDayjs(adc.delivery_end_date)
                    ? adc.delivery_end_date
                    : dayjs(adc.delivery_end_date)
                  : null;

                return {
                  ...adc,
                  delivery_start_date: deliveryStartDate,
                  delivery_end_date: deliveryEndDate,
                  listing_delivery_condition_id:
                    adc.listing_delivery_condition_id ?? null,
                } as ApplicationDeliveryCondition & {
                  delivery_start_date?: dayjs.Dayjs | null;
                  delivery_end_date?: dayjs.Dayjs | null;
                };
              }
            );
          }
          return [];
        }

        const listingDeliveryConditions = listingData.delivery_conditions;

        if (applicationData?.delivery_conditions) {
          const applicationDeliveriesMap = new Map<
            number | null,
            ApplicationDeliveryCondition
          >();

          applicationData.delivery_conditions.forEach(
            (adc: ApplicationDeliveryCondition) => {
              const key = adc.listing_delivery_condition_id ?? null;
              applicationDeliveriesMap.set(key, adc);
            }
          );

          const mergedDeliveries: (ApplicationDeliveryCondition & {
            delivery_start_date?: dayjs.Dayjs | null;
            delivery_end_date?: dayjs.Dayjs | null;
            listing_delivery_condition?: DeliveryConditionsData;
          })[] = [];

          listingDeliveryConditions.forEach((ldc: DeliveryConditionsData) => {
            const applicationDelivery = applicationDeliveriesMap.get(
              ldc.id ?? null
            );

            if (applicationDelivery) {
              const deliveryStartDate = applicationDelivery.delivery_start_date
                ? dayjs.isDayjs(applicationDelivery.delivery_start_date)
                  ? applicationDelivery.delivery_start_date
                  : dayjs(applicationDelivery.delivery_start_date)
                : null;
              const deliveryEndDate = applicationDelivery.delivery_end_date
                ? dayjs.isDayjs(applicationDelivery.delivery_end_date)
                  ? applicationDelivery.delivery_end_date
                  : dayjs(applicationDelivery.delivery_end_date)
                : null;

              if (applicationDelivery.is_accepting_all) {
                const merged = {
                  ...applicationDelivery,
                  listing_delivery_condition_id: ldc.id,
                  delivery_type: ldc.delivery_type,
                  listing_delivery_condition: ldc,
                  delivery_start_date: deliveryStartDate,
                  delivery_end_date: deliveryEndDate,
                  is_accepting_all: true,
                };
                mergedDeliveries.push(merged as any);
              } else {
                const merged = {
                  ...applicationDelivery,
                  listing_delivery_condition_id: ldc.id,
                  delivery_type: ldc.delivery_type,
                  listing_delivery_condition: ldc,
                  delivery_start_date: deliveryStartDate,
                  delivery_end_date: deliveryEndDate,
                };
                mergedDeliveries.push(merged as any);
              }
            } else {
              if (ldc.delivery_type !== 'free_form') {
                const defaultDelivery = {
                  listing_delivery_condition_id: ldc.id,
                  delivery_type: ldc.delivery_type,
                  listing_delivery_condition: ldc,
                  delivery_price: undefined,
                  delivery_start_date: null,
                  delivery_end_date: null,
                  delivery_frequency_value: undefined,
                  delivery_frequency_unit: undefined,
                  is_accepting_all: true,
                  products: [],
                };
                mergedDeliveries.push(defaultDelivery as any);
              }
            }
          });

          applicationData.delivery_conditions.forEach(
            (adc: ApplicationDeliveryCondition) => {
              if (adc.listing_delivery_condition_id === null) {
                const deliveryStartDate = adc.delivery_start_date
                  ? dayjs.isDayjs(adc.delivery_start_date)
                    ? adc.delivery_start_date
                    : dayjs(adc.delivery_start_date)
                  : null;
                const deliveryEndDate = adc.delivery_end_date
                  ? dayjs.isDayjs(adc.delivery_end_date)
                    ? adc.delivery_end_date
                    : dayjs(adc.delivery_end_date)
                  : null;

                mergedDeliveries.push({
                  ...adc,
                  delivery_start_date: deliveryStartDate,
                  delivery_end_date: deliveryEndDate,
                } as any);
              }
            }
          );

          return mergedDeliveries;
        }

        const newDeliveries = listingDeliveryConditions
          .filter(
            (dc: DeliveryConditionsData) => dc.delivery_type !== 'free_form'
          )
          .map((dc: DeliveryConditionsData) => ({
            listing_delivery_condition_id: dc.id,
            delivery_type: dc.delivery_type,
            listing_delivery_condition: dc,
            delivery_price: undefined,
            delivery_start_date: null,
            delivery_end_date: null,
            delivery_frequency_value: undefined,
            delivery_frequency_unit: undefined,
            is_accepting_all: true,
            products: [],
          }));

        return newDeliveries;
      })(),
    };

    form.setFieldsValue(formData);

    if (applicationData?.certificates) {
      setCurrentCertificates(applicationData.certificates);
    } else if (!applicationData) {
      setCurrentCertificates([]);
    }

    const additionalData = {
      certificates: applicationData?.certificates || [],
    };

    let lastSavedBaseData;
    if (applicationData) {
      setTimeout(() => {
        const processedFormValues = form.getFieldsValue(true);
        const processedData: Record<string, unknown> = {
          ...processedFormValues,
          certificates: applicationData.certificates || [],
        };

        const compareFieldsArray = [
          'producer_comments',
          'offer_valid_from',
          'offer_valid_until',
          'warehouse_address',
          'lks_lat',
          'lks_long',
          'delivery_cost',
          'application_products',
          'delivery_conditions',
          'delivery_type',
          'certificates',
        ];

        compareFieldsArray.forEach((field) => {
          if (!(field in processedData)) {
            processedData[field] = undefined;
          }
        });

        setLastSavedData(
          processedData as unknown as Partial<ListingApplicationFormData>
        );
      }, 0);
      return;
    } else {
      lastSavedBaseData = formData;
    }

    const fullCurrentData = {
      ...lastSavedBaseData,
      ...additionalData,
    };

    const compareFieldsArray = [
      'producer_comments',
      'offer_valid_from',
      'offer_valid_until',
      'warehouse_address',
      'lks_lat',
      'lks_long',
      'delivery_cost',
      'application_products',
      'delivery_conditions',
      'delivery_type',
      'certificates',
    ];

    compareFieldsArray.forEach((field) => {
      if (!(field in fullCurrentData)) {
        (fullCurrentData as any)[field] = undefined;
      }
    });

    setLastSavedData(
      fullCurrentData as unknown as Partial<ListingApplicationFormData>
    );
  }, [isDataLoading, listingData, applicationData, form]);

  const farmQualityMarkIds =
    farmQualityMarks?.map((qmark: FarmQualityMark) => qmark.value) || [];

  const farmQualityMarksArray =
    farmQualityMarks?.map((qmark: FarmQualityMark) => ({
      value: qmark.value,
      label: qmark.label,
    })) || [];

  const handleSuccess = () => {
    resetEverything();
    onClose();
    setReload((old) => old + 1);
  };

  const processProductsData = (productsList: ApplicationProductData[]) => {
    const filledProducts = productsList.filter((p) => {
      if (
        !p?.farm_product_id ||
        p.offered_price == null ||
        !p.offered_price_unit_id
      ) {
        return false;
      }
      const offerType =
        p.offer_type ??
        (p.offered_quantity != null && p.offered_quantity !== undefined
          ? 'other_amount'
          : 'full_amount');
      if (offerType === 'other_amount') {
        return p.offered_quantity != null && !!p.offered_unit_id;
      }
      return true;
    });

    return filledProducts.map((p) => {
      const processed: ProcessedProduct = {
        farm_product_id: p.farm_product_id,
        listing_product_id: p.listing_product_id,
        offered_price:
          p.offered_price !== null && p.offered_price !== undefined
            ? Number(p.offered_price)
            : undefined,
        offered_price_unit_id: p.offered_price_unit_id,
        offered_quantity:
          p.offered_quantity !== null && p.offered_quantity !== undefined
            ? Number(p.offered_quantity)
            : undefined,
        offered_unit_id: p.offered_unit_id,
      };

      if (!applicationId && 'id' in p) {
        delete processed.id;
      }

      return processed;
    });
  };

  const processDeliveryData = (deliveries: ApplicationDeliveryCondition[]) => {
    const hasFreeForm = cachedListing?.delivery_conditions?.some(
      (dc: DeliveryConditionsData) => dc.delivery_type === 'free_form'
    );
    return deliveries.map((delivery, index) => {
      const isAcceptingAll =
        delivery.is_accepting_all !== undefined &&
        delivery.is_accepting_all !== null
          ? delivery.is_accepting_all
          : hasFreeForm
            ? true
            : false;

      let listingCondition: DeliveryConditionsData | undefined;
      if (
        delivery.listing_delivery_condition_id &&
        cachedListing?.delivery_conditions
      ) {
        listingCondition = cachedListing.delivery_conditions.find(
          (lc: DeliveryConditionsData) =>
            lc.id === delivery.listing_delivery_condition_id
        );
      }

      let productsArray: Array<{
        listing_product_id: string;
        product_quantity: number;
      }> = [];

      if (
        isAcceptingAll &&
        !delivery.listing_delivery_condition_id &&
        delivery.products
      ) {
        productsArray = delivery.products.map(
          (dp: { listing_product_id: string; product_quantity: number }) => ({
            listing_product_id: dp.listing_product_id,
            product_quantity: dp.product_quantity,
          })
        );
      } else if (isAcceptingAll) {
        if (listingCondition) {
          if (
            listingCondition?.products &&
            Array.isArray(listingCondition.products) &&
            listingCondition.products.length > 0
          ) {
            productsArray = (
              listingCondition.products as DeliveryProductReference[]
            )
              .filter(
                (
                  p
                ): p is DeliveryProductReference & {
                  listing_product_id: string;
                } => !!p.listing_product_id
              )
              .map((p) => ({
                listing_product_id: p.listing_product_id,
                product_quantity: parseFloat(
                  String(p.delivery_quantity ?? p.product_quantity ?? 0)
                ),
              }));
          } else {
            productsArray = displayProducts
              .filter((p) => p.listing_product_id)
              .map((p) => ({
                listing_product_id: p.listing_product_id,
                product_quantity:
                  p.offered_quantity && p.offered_quantity > 0
                    ? p.offered_quantity
                    : p.product_required_quantity || 1,
              }));
          }
        } else {
          productsArray = displayProducts
            .filter((p) => p.listing_product_id)
            .map((p) => ({
              listing_product_id: p.listing_product_id,
              product_quantity:
                p.offered_quantity && p.offered_quantity > 0
                  ? p.offered_quantity
                  : p.product_required_quantity || 1,
            }));
        }
      } else {
        productsArray = (delivery.products || []).map(
          (dp: { listing_product_id: string; product_quantity: number }) => ({
            listing_product_id: dp.listing_product_id,
            product_quantity: dp.product_quantity,
          })
        );
      }

      const processed: ProcessedDelivery = {
        listing_delivery_condition_id:
          delivery.listing_delivery_condition_id ?? null,
        delivery_price: delivery.delivery_price ?? null,
        delivery_address:
          isAcceptingAll && listingCondition?.delivery_address
            ? listingCondition.delivery_address
            : (delivery.delivery_address ?? null),
        delivery_start_date: formatDeliveryDate(
          delivery.delivery_start_date,
          listingCondition?.delivery_start_date,
          isAcceptingAll
        ),
        delivery_end_date: formatDeliveryDate(
          delivery.delivery_end_date,
          listingCondition?.delivery_end_date,
          isAcceptingAll
        ),
        delivery_frequency_value:
          isAcceptingAll && listingCondition?.delivery_frequency_value != null
            ? listingCondition.delivery_frequency_value
            : (delivery.delivery_frequency_value ?? null),
        delivery_frequency_unit:
          isAcceptingAll && listingCondition?.delivery_frequency_unit
            ? listingCondition.delivery_frequency_unit
            : (delivery.delivery_frequency_unit ?? null),
        is_accepting_all: isAcceptingAll,
        products: productsArray,
      };

      if (!applicationId && delivery.id) {
        delete processed.id;
      }

      return processed;
    });
  };

  const stopAutoSave = () => {};

  useEffect(() => {
    if (!open) {
      stopAutoSave();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setDraftId(null);
      setIsFirstSave(true);
      setLastSavedData(null);
      setDraftSaveStatus({ status: 'idle' });

      if (!applicationId) {
        form.setFieldValue('application_products', []);
        form.setFieldValue('delivery_conditions', []);
        setCurrentCertificates([]);
      }
    }
  }, [open, applicationId, form, listingId]);

  const steps = useMemo(() => {
    return [
      {
        key: 'basic_info',
        label: intl.formatMessage({
          id: 'listings.application.basic_information',
        }),
        content: (
          <NewApplicationForm
            listingData={cachedListing}
            isEditing={!!applicationId}
            farmQualityMarks={farmQualityMarksArray}
            initialFarmQualityMarkIds={farmQualityMarkIds}
            initialCertificates={currentCertificates}
            applicationId={applicationId || draftId}
            onCertificatesChange={(certs) => {
              setCurrentCertificates(certs);
            }}
            hasProductsMissingQualityMarks={hasProductsMissingQualityMarks}
            farmData={
              farmData?.registered_full_address
                ? (() => {
                    const coords = farmData.registered_address_coordinates
                      ? parseCoordinates(
                          farmData.registered_address_coordinates
                        )
                      : null;
                    return {
                      warehouse_address: farmData.registered_full_address,
                      lks_lat: coords?.lat,
                      lks_long: coords?.long,
                    };
                  })()
                : undefined
            }
            allQualityMarks={allQualityMarks}
            ignoreDeadlineValidation={!!ignoreDeadlineValidation}
          />
        ),
      },
      {
        key: 'products',
        label: intl.formatMessage({ id: 'listings.application.products' }),
        content: (
          <NewApplicationProductsForm
            key={applicationId || 'new'}
            listingData={cachedListing}
            availableProducts={availableProductsByCategory}
            farmQualityMarks={farmQualityMarksArray}
            storageUnits={storageUnits}
            allQualityMarks={allQualityMarks}
          />
        ),
      },
      {
        key: 'delivery',
        label: intl.formatMessage({
          id: 'listings.application.delivery_conditions',
        }),
        disabled: displayProducts.length === 0,
        content: (
          <NewApplicationDeliveryForm
            key={applicationId || 'new'}
            isEditing={!!applicationId}
            listingData={cachedListing}
            storageUnits={storageUnits}
            farmProductNames={farmProductNames}
          />
        ),
      },
      {
        key: 'contract',
        label: intl.formatMessage({
          id: 'listings.application.contract_terms',
        }),
        content: (
          <NewApplicationContractForm
            listingData={cachedListing}
            isEditing={!!applicationId}
          />
        ),
      },
      {
        key: 'summary',
        label: intl.formatMessage({ id: 'listings.application.summary' }),
        content: (
          <ApplicationSummary
            applicationData={cachedApplication}
            listingData={cachedListing}
            certificates={currentCertificates}
            form={form}
            farmProductNames={farmProductNames}
            availableProductsByCategory={availableProductsByCategory}
            onCertificateDelete={handleCertificateDelete}
            allQualityMarks={allQualityMarks}
          />
        ),
      },
    ];
  }, [
    applicationId,
    cachedListing,
    farmQualityMarksArray,
    farmQualityMarkIds,
    storageUnits,
    displayProducts,
    currentCertificates,
    cachedApplication,
    form,
    formProductsForSummary,
    farmProductNames,
    intl,
    availableProductsByCategory,
    tabValidationErrors,
    allQualityMarks,
    farmData,
    draftId,
    hasProductsMissingQualityMarks,
  ]);

  const hasValidationErrors = useCallback(() => {
    const fieldsError = form.getFieldsError();
    const hasErrors = fieldsError.some(({ errors }) => errors.length > 0);

    const certificatesError =
      hasProductsMissingQualityMarks &&
      currentCertificates.filter(
        (c) => !c.isPending || c.file_id || c.originFile
      ).length === 0;

    if (hasErrors || certificatesError) {
      const newTabErrors: { [tabKey: string]: number } = {};

      fieldsError.forEach(({ name, errors }) => {
        if (errors.length > 0) {
          const fieldPath = Array.isArray(name) ? name : [name];
          const firstPathSegment = String(fieldPath[0]);

          if (stepFieldMap.basic_info.includes(firstPathSegment)) {
            newTabErrors.basic_info = (newTabErrors.basic_info || 0) + 1;
          } else if (
            firstPathSegment === 'application_products' ||
            stepFieldMap.products.includes(firstPathSegment)
          ) {
            newTabErrors.products = (newTabErrors.products || 0) + 1;
          } else if (
            firstPathSegment === 'delivery_conditions' ||
            stepFieldMap.delivery.includes(firstPathSegment)
          ) {
            newTabErrors.delivery = (newTabErrors.delivery || 0) + 1;
          }
        }
      });

      if (certificatesError) {
        newTabErrors.basic_info = (newTabErrors.basic_info || 0) + 1;
      }

      setTabValidationErrors(newTabErrors);
    } else {
      setTabValidationErrors({});
    }

    return hasErrors || certificatesError;
  }, [form, hasProductsMissingQualityMarks, currentCertificates]);

  function isValidationError(
    error: unknown
  ): error is { errorFields: FieldError[] } {
    return (
      typeof error === 'object' && error !== null && 'errorFields' in error
    );
  }

  const hasAtLeastOneCompleteProduct = useCallback((): boolean => {
    const products =
      (form.getFieldValue('application_products') as
        | ApplicationProductData[]
        | undefined) || [];
    return products.some((product, index) => {
      if (
        !product?.farm_product_id ||
        product.offered_price == null ||
        !product.offered_price_unit_id
      ) {
        return false;
      }
      const offerType =
        product.offer_type ??
        form.getFieldValue(['application_products', index, 'offer_type']);
      if (offerType === 'other_amount') {
        return product.offered_quantity != null && !!product.offered_unit_id;
      }
      return true;
    });
  }, [form]);

  const validateCurrentStep = async (
    preserveOtherTabErrors = false,
    showErrorMessage = true
  ): Promise<boolean> => {
    const currentKey = steps[currentStep].key;

    if (currentKey === 'contract' || currentKey === 'summary') {
      return true;
    }

    try {
      await form.validateFields();

      const allFieldsErrors = form.getFieldsError();
      const currentStepErrors: FieldError[] = [];
      const otherStepErrors: FieldError[] = [];

      allFieldsErrors.forEach((field) => {
        if (field.errors.length === 0) return;

        const fieldPath = Array.isArray(field.name) ? field.name : [field.name];
        const firstPathSegment = String(fieldPath[0]);

        let belongsToCurrentStep = false;

        if (currentKey === 'products') {
          belongsToCurrentStep = firstPathSegment === 'application_products';
        } else if (currentKey === 'delivery') {
          belongsToCurrentStep =
            firstPathSegment === 'delivery_conditions' ||
            stepFieldMap.delivery.includes(firstPathSegment);
        } else {
          belongsToCurrentStep =
            stepFieldMap[currentKey]?.includes(firstPathSegment) || false;
        }

        if (belongsToCurrentStep) {
          currentStepErrors.push(field);
        } else {
          otherStepErrors.push(field);
        }
      });

      if (!preserveOtherTabErrors && otherStepErrors.length > 0) {
        const fieldsToClear = otherStepErrors.map((field) => ({
          name: field.name,
          errors: [],
        }));
        form.setFields(fieldsToClear);
      }

      if (currentKey === 'basic_info' && hasProductsMissingQualityMarks) {
        const validCertificates = currentCertificates.filter(
          (c) => !c.isPending || c.file_id || c.originFile
        );
        if (validCertificates.length === 0) {
          hasValidationErrors();
          if (showErrorMessage) {
            message.error(
              intl.formatMessage({
                id: 'validation.product_missing_quality_marks_warning',
              })
            );
          }
          return false;
        }
      }

      if (
        currentKey === 'products' &&
        cachedListing?.products &&
        cachedListing.products.length > 0 &&
        !hasAtLeastOneCompleteProduct()
      ) {
        setTabValidationErrors((prev) => ({ ...prev, products: 1 }));
        if (showErrorMessage) {
          message.error(
            intl.formatMessage({
              id: 'validation.at_least_one_product_required',
            })
          );
        }
        return false;
      }

      if (currentStepErrors.length > 0) {
        hasValidationErrors();
        if (showErrorMessage) {
          message.error(intl.formatMessage({ id: 'validation.fix_errors' }));
        }
        return false;
      }
    } catch (e: unknown) {
      if (isValidationError(e)) {
        const currentStepErrorFields: FieldError[] = [];
        const otherStepErrorFields: FieldError[] = [];

        e.errorFields.forEach((field) => {
          const fieldPath = Array.isArray(field.name)
            ? field.name
            : [field.name];
          const firstPathSegment = String(fieldPath[0]);

          let belongsToCurrentStep = false;

          if (currentKey === 'products') {
            belongsToCurrentStep = firstPathSegment === 'application_products';
          } else if (currentKey === 'delivery') {
            belongsToCurrentStep =
              firstPathSegment === 'delivery_conditions' ||
              stepFieldMap.delivery.includes(firstPathSegment);
          } else {
            belongsToCurrentStep =
              stepFieldMap[currentKey]?.includes(firstPathSegment) || false;
          }

          if (belongsToCurrentStep) {
            currentStepErrorFields.push(field);
          } else {
            otherStepErrorFields.push(field);
          }
        });

        if (!preserveOtherTabErrors && otherStepErrorFields.length > 0) {
          const fieldsToClear = otherStepErrorFields.map((field) => ({
            name: field.name,
            errors: [],
          }));
          form.setFields(fieldsToClear);
        }

        if (currentStepErrorFields.length > 0) {
          hasValidationErrors();
          if (showErrorMessage) {
            message.error(intl.formatMessage({ id: 'validation.fix_errors' }));
          }
          return false;
        }
      } else {
        hasValidationErrors();
        if (showErrorMessage) {
          message.error(intl.formatMessage({ id: 'validation.fix_errors' }));
        }
        return false;
      }
    }

    if (currentKey === 'basic_info' && hasProductsMissingQualityMarks) {
      const validCertificates = currentCertificates.filter(
        (c) => !c.isPending || c.file_id || c.originFile
      );
      if (validCertificates.length === 0) {
        hasValidationErrors();
        if (showErrorMessage) {
          message.error(
            intl.formatMessage({
              id: 'validation.product_missing_quality_marks_warning',
            })
          );
        }
        return false;
      }
    }

    if (currentKey === 'delivery') {
      const qtyError = validateDeliveryQuantities();
      if (qtyError) {
        setTabValidationErrors((prev) => ({ ...prev, delivery: 1 }));
        if (showErrorMessage) {
          message.error(qtyError);
        }
        return false;
      }
    }

    if (tabValidationErrors[currentKey]) {
      setTabValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[currentKey];
        return newErrors;
      });
    }

    return true;
  };

  const validateAllSteps = async (): Promise<boolean> => {
    setIsValidatingAllSteps(true);
    const errors: { [tabKey: string]: number } = {
      basic_info: 0,
      products: 0,
      delivery: 0,
    };

    const formValues = form.getFieldsValue();
    const deliveries =
      (form.getFieldValue('delivery_conditions') as
        | ApplicationDeliveryCondition[]
        | undefined) || [];

    if (!ignoreDeadlineValidation) {
      const effectiveOfferValidFrom = formValues.offer_valid_from || dayjs();
      if (cachedListing?.publication_start_date) {
        const offerValidFrom = dayjs(effectiveOfferValidFrom);
        const listingStartDate = dayjs(cachedListing.publication_start_date);
        if (offerValidFrom.isBefore(listingStartDate)) {
          errors.basic_info += 1;
        }
      }

      if (formValues.offer_valid_until) {
        const effectiveOfferValidFrom = formValues.offer_valid_from || dayjs();
        const offerValidFrom = dayjs(effectiveOfferValidFrom);
        const offerValidUntil = dayjs(formValues.offer_valid_until).endOf(
          'day'
        );
        if (offerValidUntil.isBefore(offerValidFrom)) {
          errors.basic_info += 1;
        }
      }
    }

    if (
      !formValues.warehouse_address ||
      String(formValues.warehouse_address).trim() === ''
    ) {
      errors.basic_info += 1;
    }

    if (
      cachedListing?.products &&
      cachedListing.products.length > 0 &&
      !hasAtLeastOneCompleteProduct()
    ) {
      errors.products += 1;
    }

    if (deliveries.length === 0) {
      if (
        cachedListing?.delivery_conditions &&
        cachedListing.delivery_conditions.length > 0
      ) {
        errors.delivery = 1;
      }
    } else {
      deliveries.forEach(
        (delivery: ApplicationDeliveryCondition, index: number) => {
          if (delivery.delivery_price == null) {
            errors.delivery += 1;
          } else if (
            typeof delivery.delivery_price === 'number' &&
            delivery.delivery_price < 0
          ) {
            errors.delivery += 1;
          }

          const isAcceptingAll =
            delivery.is_accepting_all ??
            form.getFieldValue([
              'delivery_conditions',
              index,
              'is_accepting_all',
            ]);
          if (!isAcceptingAll) {
            if (!delivery.delivery_start_date) {
              errors.delivery += 1;
            }
            if (!delivery.delivery_end_date) {
              errors.delivery += 1;
            }
            if (delivery.delivery_frequency_value == null) {
              errors.delivery += 1;
            } else if (
              typeof delivery.delivery_frequency_value === 'number' &&
              delivery.delivery_frequency_value <= 0
            ) {
              errors.delivery += 1;
            }
            if (!delivery.delivery_frequency_unit) {
              errors.delivery += 1;
            }
          }
        }
      );
    }

    const qtyError = validateDeliveryQuantities();
    if (qtyError) {
      errors.delivery += 1;
    }

    setTabValidationErrors(errors);

    const hasErrors = Object.values(errors).some((count) => count > 0);

    if (hasErrors) {
      setIsValidatingAllSteps(false);
      if (qtyError) {
        message.error(qtyError);
      } else if (errors.products > 0) {
        message.error(
          intl.formatMessage({ id: 'validation.at_least_one_product_required' })
        );
      } else {
        message.error(intl.formatMessage({ id: 'validation.fix_errors' }));
      }
      return false;
    }

    setIsValidatingAllSteps(false);
    return true;
  };

  const validateDeliveryQuantities = (): string | null => {
    const deliveries =
      (form.getFieldValue(
        'delivery_conditions'
      ) as ApplicationDeliveryCondition[]) || [];
    if (!cachedListing?.products || deliveries.length === 0) return null;

    const listingProducts = cachedListing.products.filter((p) => !!p.id);

    const availableByProduct: Record<string, number> = {};
    listingProducts.forEach((p) => {
      if (p.id) {
        const applicationProduct = displayProducts.find(
          (ap) => ap.listing_product_id === p.id
        );
        const availableQuantity = parseFloat(
          (applicationProduct?.offered_quantity &&
          applicationProduct.offered_quantity > 0
            ? applicationProduct.offered_quantity
            : p.required_quantity || 0
          ).toString()
        );
        availableByProduct[p.id] = isNaN(availableQuantity)
          ? 0
          : availableQuantity;
      }
    });

    const usedByProduct: Record<string, number> = {};

    deliveries.forEach((delivery) => {
      let condition = (
        delivery as ApplicationDeliveryCondition & {
          listing_delivery_condition?: DeliveryConditionsData;
        }
      ).listing_delivery_condition;

      if (
        !condition &&
        delivery.listing_delivery_condition_id &&
        cachedListing?.delivery_conditions
      ) {
        condition =
          cachedListing.delivery_conditions.find(
            (lc: DeliveryConditionsData) =>
              lc.id === delivery.listing_delivery_condition_id
          ) || undefined;
      }

      const isAcceptingAll = !!delivery.is_accepting_all;

      if (condition) {
        if (isAcceptingAll) {
          if (condition.delivery_type === 'detailed_delivery') {
            if (condition.is_all_products) {
              if (
                Array.isArray(condition.products) &&
                condition.products.length > 0
              ) {
                (condition.products as DeliveryProductReference[]).forEach(
                  (p) => {
                    const pid =
                      p.listing_product_id ?? (p.id ? String(p.id) : null);

                    if (!pid) return;

                    const add = parseFloat(
                      String(p.product_quantity ?? p.delivery_quantity ?? 0)
                    );

                    usedByProduct[pid] =
                      (usedByProduct[pid] || 0) + (isNaN(add) ? 0 : add);
                  }
                );
              } else {
                listingProducts.forEach((lp) => {
                  if (!lp.id) return;

                  const available = availableByProduct[lp.id] || 0;
                  const alreadyUsed = usedByProduct[lp.id] || 0;
                  const remaining = Math.max(0, available - alreadyUsed);

                  usedByProduct[lp.id] =
                    (usedByProduct[lp.id] || 0) + remaining;
                });
              }
            } else if (Array.isArray(condition.products)) {
              (condition.products as DeliveryProductReference[]).forEach(
                (p) => {
                  const pid =
                    p.listing_product_id ?? (p.id ? String(p.id) : null);

                  if (!pid) return;

                  const add = parseFloat(
                    String(p.product_quantity ?? p.delivery_quantity ?? 0)
                  );

                  usedByProduct[pid] =
                    (usedByProduct[pid] || 0) + (isNaN(add) ? 0 : add);
                }
              );
            }
          } else if (condition.delivery_type === 'delivery_conditions') {
            listingProducts.forEach((lp) => {
              if (!lp.id) return;

              const available = availableByProduct[lp.id] || 0;

              usedByProduct[lp.id] = (usedByProduct[lp.id] || 0) + available;
            });
          }
        } else {
          if (Array.isArray(delivery.products)) {
            delivery.products.forEach((p: DeliveryProductReference) => {
              const pid = p.listing_product_id;

              if (!pid) return;

              const add = parseFloat(String(p.product_quantity ?? 0));

              if (!isNaN(add) && add > 0) {
                usedByProduct[pid] = (usedByProduct[pid] || 0) + add;
              }
            });
          }
        }
      } else {
        const isAcceptingAllNew = !!delivery.is_accepting_all;

        if (isAcceptingAllNew) {
          listingProducts.forEach((lp) => {
            if (!lp.id) return;

            const available = availableByProduct[lp.id] || 0;
            const alreadyUsed = usedByProduct[lp.id] || 0;
            const remaining = Math.max(0, available - alreadyUsed);

            usedByProduct[lp.id] = (usedByProduct[lp.id] || 0) + remaining;
          });
        } else if (Array.isArray(delivery.products)) {
          delivery.products.forEach((p: DeliveryProductReference) => {
            const pid = p.listing_product_id;

            if (!pid) return;

            const add = parseFloat(String(p.product_quantity ?? 0));

            if (!isNaN(add) && add > 0) {
              usedByProduct[pid] = (usedByProduct[pid] || 0) + add;
            }
          });
        }
      }
    });

    const errors: string[] = [];

    Object.entries(availableByProduct).forEach(([idStr, available]) => {
      const used = usedByProduct[idStr] || 0;

      if (used < 0) {
        errors.push(
          intl.formatMessage({ id: 'validation.quantity_exceeds_available' })
        );
      } else if (used > available) {
        errors.push(
          intl.formatMessage({ id: 'validation.quantity_exceeds_available' })
        );
      } else if (used < available) {
        errors.push(
          intl.formatMessage({
            id: 'listings.application.quantity_differs',
          })
        );
      }
    });

    if (errors.length > 0) {
      return errors[0];
    }
    return null;
  };

  const performDraftSave = async (isAutoSave: boolean = false) => {
    const formData = form.getFieldsValue(true);

    const submitData = {
      listing_id: actualListingId,
      producer_comment: formData.producer_comments,
      offer_valid_from: (formData.offer_valid_from
        ? dayjs(formData.offer_valid_from)
        : dayjs()
      ).toISOString(),
      offer_valid_until: formData.offer_valid_until
        ? dayjs(formData.offer_valid_until).endOf('day').toISOString()
        : undefined,
      warehouse_address: formData.warehouse_address,
      lks_lat: formData.lks_lat,
      lks_long: formData.lks_long,
      delivery_cost: formData.delivery_cost,
      application_products: processProductsData(
        form.getFieldValue('application_products') || []
      ),
      delivery_conditions: processDeliveryData(
        form.getFieldValue('delivery_conditions') || []
      ),
    };

    saveDraftData(submitData, {}, { isAutoSave });
  };

  const normalizeFormDataForComparison = (
    data: Record<string, unknown>
  ): Record<string, unknown> => {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const normalized: Record<string, unknown> = { ...data };

    const isDayjs = (value: unknown): value is dayjs.Dayjs => {
      return (
        value !== null &&
        typeof value === 'object' &&
        'toISOString' in value &&
        typeof (value as { toISOString?: () => string }).toISOString ===
          'function'
      );
    };

    Object.keys(normalized).forEach((key) => {
      const value = normalized[key];

      if (typeof value === 'string' && value.trim() === '') {
        normalized[key] = null;
      }

      if (key === 'offer_valid_from') {
        if (isDayjs(value)) {
          normalized[key] = value.toISOString();
        } else if (value === null || value === undefined) {
          normalized[key] = null;
        }
      } else if (key === 'offer_valid_until') {
        if (isDayjs(value)) {
          normalized[key] = value.endOf('day').toISOString();
        } else if (value === null || value === undefined) {
          normalized[key] = null;
        }
      } else if (key === 'contract_start_date' || key === 'contract_end_date') {
        if (isDayjs(value)) {
          normalized[key] = value.toISOString();
        } else if (value === null || value === undefined) {
          normalized[key] = null;
        }
      } else if (key === 'delivery_conditions' && Array.isArray(value)) {
        normalized[key] = value.map((delivery) => {
          if (!delivery || typeof delivery !== 'object') {
            return delivery;
          }
          const normalizedDelivery = { ...delivery };
          if (isDayjs(normalizedDelivery.delivery_start_date)) {
            normalizedDelivery.delivery_start_date = (
              normalizedDelivery.delivery_start_date as dayjs.Dayjs
            ).toISOString();
          }
          if (isDayjs(normalizedDelivery.delivery_end_date)) {
            normalizedDelivery.delivery_end_date = (
              normalizedDelivery.delivery_end_date as dayjs.Dayjs
            ).toISOString();
          }
          return normalizedDelivery;
        });
      }
    });

    return normalized;
  };

  const compareFieldsArray = [
    'producer_comments',
    'offer_valid_from',
    'offer_valid_until',
    'warehouse_address',
    'delivery_cost',
    'application_products',
    'delivery_conditions',
    'delivery_type',
    'certificates',
  ];

  const {
    hasUnsavedChanges,
    checkForUnsavedChanges,
    resetAutoSave,
    setDraftSaveStatus,
  } = useAutoSave({
    form,
    isOpen: open,
    isSaving: isDraftSaving,
    onSave: () => performDraftSave(true),
    getLastSavedData: () => lastSavedData,
    setLastSavedData: (data) =>
      setLastSavedData(data as Partial<ListingApplicationFormData>),
    getAdditionalData: () => {
      const additionalData = {
        certificates: currentCertificates,
      };

      return additionalData;
    },
    isEnabled: () => {
      return (
        !applicationId ||
        (cachedApplication?.status &&
          isEditableStatus(cachedApplication.status)) === true
      );
    },
    onValidationError: hasValidationErrors,
    normalizeData: normalizeFormDataForComparison,
    compareFields: compareFieldsArray,
  });

  useEffect(() => {
    if (open && lastSavedData && !isDataLoading) {
      checkForUnsavedChanges();
    }
  }, [lastSavedData, open, isDataLoading, checkForUnsavedChanges]);

  useEffect(() => {
    if (open) {
      setDraftId(null);
      setIsFirstSave(true);
      setLastSavedData(null);
      resetAutoSave();

      if (!applicationId) {
        form.setFieldValue('application_products', []);
        form.setFieldValue('delivery_conditions', []);
        setCurrentCertificates([]);
      }
    }
  }, [open, applicationId, form, listingId, resetAutoSave]);

  useEffect(() => {
    if (isValidatingAllSteps) {
      return;
    }

    const currentKey = steps[currentStep]?.key;
    const isSummaryOrContract =
      currentKey === 'summary' || currentKey === 'contract';
    if (
      !isSummaryOrContract &&
      currentKey &&
      tabValidationErrors[currentKey] &&
      tabValidationErrors[currentKey] > 0
    ) {
      const timeoutId = setTimeout(() => {
        void validateCurrentStep(true, false);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [currentStep, isValidatingAllSteps]);

  const saveDraftPromise = async (context?: string): Promise<number | null> => {
    if (
      isDraftSaving ||
      (applicationId && !isEditableStatus(cachedApplication?.status))
    ) {
      return applicationId || draftId || null;
    }

    const validateAll = context === 'submitting';
    if (validateAll) {
      const isValid = await validateAllSteps();
      if (!isValid) {
        return null;
      }
    }

    return new Promise<number | null>((resolve, reject) => {
      const originalResolve = window.saveDraftResolve;
      const originalReject = window.saveDraftReject;

      let isResolved = false;
      const isExplicitSave = context === 'explicit_save';

      window.saveDraftResolve = (appId?: number | null) => {
        if (isResolved) return;
        isResolved = true;

        const resolvedAppId = appId || draftId || applicationId || null;
        if (originalResolve) {
          originalResolve(resolvedAppId);
        }
        resolve(resolvedAppId);
        window.saveDraftResolve = originalResolve || undefined;
        window.saveDraftReject = originalReject || undefined;

        if (isExplicitSave) {
          resetEverything();
          onClose();
        }
      };

      window.saveDraftReject = () => {
        if (isResolved) return;
        isResolved = true;

        if (originalReject) {
          originalReject();
        }
        reject();
        window.saveDraftResolve = originalResolve || undefined;
        window.saveDraftReject = originalReject || undefined;
      };

      setDraftSaveStatus({ status: 'saving' });
      message.loading(intl.formatMessage({ id: 'general.saving_draft' }), 0);

      performDraftSave(false);
    });
  };

  const nextStep = async () => {
    if (currentStep === steps.length - 2) {
      try {
        const savedAppId = await saveDraftPromise();
        if (!savedAppId) {
          return;
        }
      } catch (error: unknown) {
        message.error(intl.formatMessage({ id: 'validation.error' }));
        return;
      }
    }

    setCurrentStep(currentStep + 1);
  };

  const resetEverything = () => {
    setCachedApplication(null);
    setCachedListing(null);
    form.resetFields();
    form.setFieldValue('application_products', []);
    form.setFieldValue('delivery_conditions', []);
    setCurrentCertificates([]);
    setCurrentStep(0);
    setDraftId(null);
    setIsFirstSave(true);
    setLastSavedData(null);
    resetAutoSave();
    setTabValidationErrors({});
    setIsSubmitting(false);
  };

  const handleClose = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (!hasUnsavedChanges) {
      resetEverything();
      onClose();
      return;
    }

    Modal.confirm({
      title: intl.formatMessage({ id: 'general.confirm_close' }),
      content: intl.formatMessage({
        id: 'general.do_you_really_want_to_cancel_creation',
      }),
      okText: intl.formatMessage({ id: 'general.cancel' }),
      cancelText: intl.formatMessage({ id: 'general.go_back' }),
      onOk: () => {
        resetEverything();
        onClose();
      },
    });
  };

  const handleFormValuesChange = (
    changedValues: Record<string, unknown>,
    allValues: Record<string, unknown>
  ) => {
    const hasTrackedFieldChanges = Object.keys(changedValues).some((key) =>
      [
        'producer_comments',
        'offer_valid_from',
        'offer_valid_until',
        'warehouse_address',
        'lks_lat',
        'lks_long',
        'application_products',
        'delivery_conditions',
        'certificates',
      ].includes(key)
    );

    if (!hasTrackedFieldChanges) {
      const currentFormValues = form.getFieldsValue(true);
      const updatedLastSaved: Record<string, unknown> = {
        ...currentFormValues,
        certificates: currentCertificates,
      };

      const compareFieldsArray = [
        'producer_comments',
        'offer_valid_from',
        'offer_valid_until',
        'warehouse_address',
        'delivery_cost',
        'application_products',
        'delivery_conditions',
        'delivery_type',
        'certificates',
      ];

      compareFieldsArray.forEach((field) => {
        if (!(field in updatedLastSaved)) {
          updatedLastSaved[field] = undefined;
        }
      });

      setLastSavedData(updatedLastSaved);
    }

    checkForUnsavedChanges();

    const currentStepKey = steps[currentStep].key;

    if (tabValidationErrors[currentStepKey]) {
      setTabValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[currentStepKey];
        return newErrors;
      });
    }
  };

  const prevStep = async () => {
    setCurrentStep(currentStep - 1);
  };

  const changeStep = async (desiredStep: string) => {
    try {
      const index = steps.findIndex((step) => step.key === desiredStep);

      if (index === -1) {
        return;
      }

      const desiredStepObj = steps[index];
      if (desiredStepObj.disabled) {
        message.warning(
          intl.formatMessage({
            id: 'listings.application.save_products_first',
          })
        );
        return;
      }

      setCurrentStep(index);
    } catch (error: unknown) {}
  };

  const handleSubmit = async () => {
    if (
      cachedApplication?.status === 'submitted' ||
      cachedApplication?.is_pending_submitting
    ) {
      message.error(
        intl.formatMessage({ id: 'validation.cannot_submit_already_submitted' })
      );
      return;
    }

    if (hasProductsMissingQualityMarks) {
      const validCertificates = currentCertificates.filter(
        (c) => !c.isPending || c.file_id || c.originFile
      );
      if (validCertificates.length === 0) {
        message.error(
          intl.formatMessage({
            id: 'validation.product_missing_quality_marks_warning',
          })
        );
        const basicInfoIndex = steps.findIndex((s) => s.key === 'basic_info');
        if (basicInfoIndex >= 0) {
          setCurrentStep(basicInfoIndex);
        }

        try {
          await form.validateFields(['certificates']);
        } catch {}
        return;
      }
    }

    try {
      setIsSubmitting(true);

      const savedAppId = await saveDraftPromise('submitting');

      if (!savedAppId) {
        setIsSubmitting(false);
        return;
      }

      submitApplication({}, { appId: savedAppId });
    } catch (error: unknown) {
      message.error(intl.formatMessage({ id: 'validation.error' }));
      setIsSubmitting(false);
    }
  };

  const Footer = () => (
    <StyledFooter>
      <Button
        label={intl.formatMessage({ id: 'general.cancel' })}
        onClick={handleClose}
      />

      {(!applicationId ||
        (cachedApplication?.status &&
          isEditableStatus(cachedApplication.status))) && (
        <Button
          label={intl.formatMessage({
            id: 'general.save_draft',
          })}
          type={'primary'}
          onClick={() => saveDraftPromise('explicit_save')}
          loading={isDraftSaving}
        />
      )}
      {currentStep > 0 && (
        <Button
          label={intl.formatMessage({ id: 'general.previous' })}
          onClick={prevStep}
        />
      )}
      {currentStep < steps.length - 1 && (
        <Button
          label={
            currentStep === steps.length - 2
              ? intl.formatMessage({ id: 'general.view_summary' })
              : intl.formatMessage({ id: 'general.next_step' })
          }
          type={'primary'}
          onClick={nextStep}
        />
      )}
      {currentStep >= steps.length - 2 &&
        cachedApplication?.status !== 'submitted' &&
        !cachedApplication?.is_pending_submitting &&
        !['completed', 'cancelled'].includes(
          (cachedListing || listingData)?.status || ''
        ) && (
          <Button
            label={intl.formatMessage({ id: 'general.submit_form' })}
            type={'primary'}
            onClick={handleSubmit}
            loading={isSubmitting || isSubmittingApplication}
          />
        )}
    </StyledFooter>
  );

  const drawerTitle = useMemo(() => {
    const listing = cachedListing || listingData;
    const actionTitle = intl.formatMessage({
      id: applicationId
        ? 'listings.application.edit_title'
        : 'listings.application.create_title',
    });

    if (!listing) {
      return actionTitle;
    }

    const transactionType = listing.transaction_type;
    const listingTitle = listing.title;

    return (
      <StyledDrawerTitleWrapper>
        <div className="title">
          <span className="title-action">{actionTitle}</span>
          {listingTitle && <Label label={listingTitle} />}
        </div>
        {transactionType && (
          <Tag className="transaction-type" bordered>
            {intl.formatMessage({
              id: `enum.${transactionType.toUpperCase()}`,
            })}
          </Tag>
        )}
      </StyledDrawerTitleWrapper>
    );
  }, [cachedListing, listingData, applicationId, intl]);

  return (
    <StyledCreateUpdateListingApplicationDrawer
      key={applicationId || 'new'}
      open={open}
      onClose={handleClose}
      footer={<Footer />}
      title={drawerTitle}
      width={1000}
      closeIcon={<Icon name={'global-close-icon'} type={'small'} />}
      destroyOnClose
      zIndex={1100}
    >
      <Spinner spinning={isSubmitting || isDataLoading} dontRender>
        {!isDataLoading && (
          <Form
            form={form}
            layout={'vertical'}
            onValuesChange={handleFormValuesChange}
          >
            <Tabs
              onChange={(desiredStep) => {
                changeStep(desiredStep);
              }}
              activeKey={steps[currentStep].key}
              items={steps.map((step) => ({
                key: step.key,
                label: step.label,
                children: step.content,
                forceRender: true,
                disabled: step.disabled,
              }))}
              tabValidationErrors={tabValidationErrors}
            />
          </Form>
        )}
      </Spinner>
    </StyledCreateUpdateListingApplicationDrawer>
  );
};
